import React, { useState } from 'react';
import { Modal } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EastIcon from '@mui/icons-material/East';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import {
  INTEGRATIONS,
  FEATURED_INTEGRATION,
  FEATURED_FIELD_MAPPINGS,
  IntegrationStatusPill,
  IntegrationCard,
  directionLabel,
} from './integrationPresets';

/**
 * Integrations tab (spec §5.5) — design-only. Frames BITE as the compliance
 * layer between source systems and filing/management destinations. Card grid +
 * a fleshed-out bidirectional-sync detail for the featured integration. Every
 * action opens a "coming soon" placeholder; nothing connects to a real backend.
 */
const IntegrationsTab: React.FC = () => {
  const [configuring, setConfiguring] = useState<IntegrationCard | null>(null);

  return (
    <div className='export-hub-panel'>
      <div className='eh-intg-intro'>
        <h2 className='eh-intg-intro-title'>
          BITE is the compliance layer between your systems
        </h2>
        <p className='eh-intg-intro-sub'>
          Product and order data flows in from your systems of record; vetted
          packages, screening results, and compliance flags flow back out to
          your filing and license-management destinations.
          <span className='export-hub-preview-pill'>Preview</span>
        </p>
      </div>

      {/* Ecosystem flow diagram */}
      <div className='eh-flow'>
        <div className='eh-flow-node'>
          <StorageOutlinedIcon style={{ fontSize: 24 }} />
          <span className='eh-flow-node-title'>Source Systems</span>
          <span className='eh-flow-node-sub'>ERP · PLM · CSV</span>
        </div>
        <div className='eh-flow-arrow'>
          <span className='eh-flow-arrow-label'>product / order data</span>
          <EastIcon style={{ fontSize: 20 }} />
        </div>
        <div className='eh-flow-node eh-flow-node-bite'>
          <ShieldOutlinedIcon style={{ fontSize: 24 }} />
          <span className='eh-flow-node-title'>BITE Export Hub</span>
          <span className='eh-flow-node-sub'>classify · screen · license</span>
        </div>
        <div className='eh-flow-arrow'>
          <span className='eh-flow-arrow-label'>flags / vetted packages</span>
          <EastIcon style={{ fontSize: 20 }} />
        </div>
        <div className='eh-flow-node'>
          <LocalShippingOutlinedIcon style={{ fontSize: 24 }} />
          <span className='eh-flow-node-title'>Filing &amp; License Mgmt</span>
          <span className='eh-flow-node-sub'>downstream destinations</span>
        </div>
      </div>

      {/* Integration cards */}
      <div className='eh-intg-grid'>
        {INTEGRATIONS.map((c) => (
          <div className='eh-intg-card' key={c.key}>
            <div className='eh-intg-card-head'>
              <span className='eh-intg-icon'>{c.icon}</span>
              <IntegrationStatusPill value={c.status} />
            </div>
            <div className='eh-intg-card-name'>{c.name}</div>
            <div className='eh-intg-card-desc'>{c.description}</div>
            <div className='eh-intg-card-foot'>
              <span className='eh-intg-direction'>{directionLabel(c.direction)}</span>
              <button
                type='button'
                className='eh-mini-btn'
                onClick={() => setConfiguring(c)}
              >
                {c.status === 'Connected' ? 'Manage' : 'Connect'}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Featured integration detail: bidirectional sync + field mapping */}
      {FEATURED_INTEGRATION && (
        <div className='eh-panel eh-intg-featured'>
          <div className='eh-panel-title eh-panel-title-flex'>
            <span>
              {FEATURED_INTEGRATION.name} — Field Mapping
              <span className='export-hub-preview-pill'>Coming soon</span>
            </span>
            <span className='eh-intg-direction'>
              {directionLabel(FEATURED_INTEGRATION.direction)}
            </span>
          </div>
          <p className='eh-detail-note'>
            Map incoming source fields to BITE product fields — the same column
            mapping used for CSV import. Unmapped fields are ignored; mapped
            fields update the product on each sync.
          </p>
          <table className='runs-table'>
            <thead>
              <tr>
                <th>Source Field</th>
                <th />
                <th>BITE Product Field</th>
                <th>Sample</th>
              </tr>
            </thead>
            <tbody>
              {FEATURED_FIELD_MAPPINGS.map((m) => (
                <tr key={m.source}>
                  <td>{m.source}</td>
                  <td className='eh-map-arrow'>
                    <EastIcon style={{ fontSize: 15 }} />
                  </td>
                  <td>
                    <span className='eh-eccn-chip'>{m.target}</span>
                  </td>
                  <td className='eh-map-sample'>{m.sample}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Placeholder connect/configure modal */}
      <Modal
        open={!!configuring}
        onClose={() => setConfiguring(null)}
        className='eh-modal-root'
      >
        <div className='eh-modal eh-modal-sm' role='dialog' aria-label='Configure integration'>
          <div className='eh-modal-head'>
            <h2 className='eh-modal-title'>{configuring?.name}</h2>
            <button
              className='eh-modal-close'
              onClick={() => setConfiguring(null)}
              aria-label='Close'
              type='button'
            >
              <CloseIcon style={{ fontSize: 20 }} />
            </button>
          </div>
          <div className='eh-modal-body'>
            <div className='eh-intg-modal-icon'>{configuring?.icon}</div>
            <p className='eh-detail-note' style={{ textAlign: 'center' }}>
              Connecting {configuring?.name} is coming soon. This is a preview of
              how BITE will sync with your {configuring?.category.toLowerCase()}{' '}
              systems. Reach out to your BITE contact to join the early access
              program.
            </p>
          </div>
          <div className='eh-modal-foot'>
            <button
              className='btn-primary'
              type='button'
              onClick={() => setConfiguring(null)}
            >
              Got it
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default IntegrationsTab;
